import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { fetchAnalytics, MODEL_LABELS, PERSPECTIVE_COLORS } from "../../lib/newsApi";

const Metric = ({ label, value, hint, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
    <p className="mt-2 font-serif text-4xl text-slate-900">{Math.round(value)}<span className="text-base text-slate-400">/100</span></p>
    <p className="mt-1 text-xs text-slate-500">{hint}</p>
  </div>
);

const Bars = ({ data, colors }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;
  return (
    <div>
      <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
        {Object.entries(data).map(([k, v]) => <span key={k} style={{ width: `${(100 * v) / total}%`, background: colors[k] }} title={`${k}: ${v}`} />)}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {Object.entries(data).map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: colors[k] }} />{MODEL_LABELS[k] || k} {Math.round((100 * v) / total)}%</span>)}
      </div>
    </div>
  );
};

const MODEL_COLORS = { public: "#0ea5e9", corporate: "#475569", nonprofit: "#10b981", "member-funded": "#f59e0b", independent: "#8b5cf6" };
const Change = ({ v }) => v == null ? <Minus size={14} className="text-slate-300" /> : v > 0 ? <span className="inline-flex items-center gap-0.5 text-emerald-700"><TrendingUp size={14} />+{v}</span> : v < 0 ? <span className="inline-flex items-center gap-0.5 text-rose-600"><TrendingDown size={14} />{v}</span> : <Minus size={14} className="text-slate-400" />;
const Card = ({ title, children, testId }) => <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid={testId}><h3 className="font-heading font-semibold text-slate-900">{title}</h3><div className="mt-4">{children}</div></div>;

export const AnalyticsView = ({ onOpenStory, onFilter }) => {
  const [hours, setHours] = useState(24);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); fetchAnalytics(hours).then(setD).catch(() => {}); }, [hours]);
  const div = d?.diversity;
  return (
    <div data-testid="analytics-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-serif text-2xl text-slate-900">Topic intelligence & diversity</h2>
          <p className="mt-1 text-sm text-slate-500">{d ? `${d.items} articles and videos in this window (vs ${d.previous_items} in the one before)` : "Loading…"}</p></div>
        <div className="flex rounded-full border border-slate-200 bg-white p-1 text-sm" role="tablist">
          {[[24, "24 hours"], [168, "7 days"]].map(([v, l]) => <button key={v} role="tab" aria-selected={hours === v} onClick={() => setHours(v)} data-testid={`analytics-hours-${v}`}
            className={`rounded-full px-4 py-1.5 ${hours === v ? "bg-sky-900 text-white" : "text-slate-600"}`}>{l}</button>)}
        </div>
      </div>
      {!d ? <div className="mt-6 h-64 animate-pulse rounded-3xl bg-white" /> : (
        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Metric label="Coverage balance" value={div.coverage_balance} hint={`${div.mainstream_items} mainstream vs ${div.independent_items} independent items`} testId="metric-coverage-balance" />
            <Metric label="Source diversity" value={div.source_diversity} hint="How evenly coverage spreads across all outlets" testId="metric-source-diversity" />
            <Metric label="Ownership diversity" value={div.ownership_diversity} hint="Spread across public, corporate, nonprofit, member-funded, independent" testId="metric-ownership-diversity" />
            <Metric label="Story overlap" value={div.story_overlap.overlap_pct} hint={`${div.story_overlap.both} shared · ${div.story_overlap.mainstream_only} mainstream-only · ${div.story_overlap.independent_only} independent-only`} testId="metric-story-overlap" />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Ideological diversity (approximate editorial perspective)" testId="analytics-perspectives">
              <Bars data={div.perspectives} colors={PERSPECTIVE_COLORS} />
              <p className="mt-3 text-xs text-slate-500">Labels are approximate outlet-level positions from public media-rating services, not judgments of individual articles.</p>
            </Card>
            <Card title="Ownership models" testId="analytics-ownership">
              <Bars data={div.ownership_models} colors={MODEL_COLORS} />
              <ul className="mt-4 space-y-1 text-xs text-slate-600">{Object.entries(div.owner_groups).map(([o, n]) => <li key={o} className="flex justify-between gap-3"><span className="truncate">{o}</span><span className="font-mono">{n}</span></li>)}</ul>
            </Card>
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <Card title="Trending topics" testId="analytics-trending">
              <ul className="space-y-1.5 text-sm">{d.trending_topics.slice(0, 10).map((t) => (
                <li key={t.topic}><button onClick={() => onFilter({ topic: t.topic, hours })} className="flex w-full items-center justify-between rounded-lg px-2 py-1 hover:bg-slate-50" data-testid={`trending-${t.topic}`}>
                  <span className="text-slate-700">{t.topic}</span><span className="flex items-center gap-3 text-xs"><span className="font-mono text-slate-500">{t.count}</span><Change v={t.previous ? Math.round(t.change_pct) : null} /></span></button></li>))}
              </ul>
            </Card>
            <Card title="Emerging stories" testId="analytics-emerging">
              <ul className="space-y-2 text-sm">{d.emerging_stories.map((s) => (
                <li key={s.id}><button onClick={() => onOpenStory(s.id)} className="text-left text-slate-800 hover:text-sky-900">{s.label} <span className="text-xs text-slate-500">· {s.size}</span></button></li>))}
                {d.emerging_stories.length === 0 && <li className="text-slate-400">No new multi-outlet stories yet.</li>}
              </ul>
            </Card>
            <Card title="Sentiment changes by topic" testId="analytics-sentiment">
              <ul className="space-y-1.5 text-sm">{d.sentiment_changes.slice(0, 10).map((s) => (
                <li key={s.topic} className="flex justify-between"><span className="text-slate-700">{s.topic}</span><span className="flex items-center gap-3 text-xs"><span className="font-mono text-slate-500">{Math.round(s.score)}</span><Change v={s.change} /></span></li>))}
              </ul>
            </Card>
          </div>
          <Card title="Coverage volume by source" testId="analytics-volume">
            <div className="h-[560px]">
              <ResponsiveContainer>
                <BarChart data={d.coverage_by_source} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>{d.coverage_by_source.map((s) => <Cell key={s.source_id} fill={s.side === "independent" ? "#10b981" : "#0369a1"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-slate-500"><span className="text-sky-700">■</span> Mainstream <span className="ml-3 text-emerald-600">■</span> Independent — independent outlets publish less often, so the feed's balance mode alternates sides to keep representation equal.</p>
          </Card>
        </div>
      )}
    </div>
  );
};
